import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Box,
  Text,
  HStack,
  VStack,
  Spinner,
  Input,
  Checkbox,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useUser } from '../../../../../context/UserContext'
import { useGetAllLocationsQuery } from '../../../../reports/api/reportsApi'
import SingleSelect from '../../../../reports/components/SingleSelect'
import {
  useCreateDeviceMutation,
  useGetDeviceModelsQuery,
  useUpdateDeviceMutation,
} from '../../../api/api'
import { DeviceDiscovery } from '../../../api/types'

type DeviceNameEntry = {
  name: string
  checked: boolean
}

export function PhysicalDeviceModal({
  data,
  isOpen,
  onClose,
  editDevice,
}: {
  data?: DeviceDiscovery
  isOpen: boolean
  onClose: () => void
  editDevice?: any
}) {
  const isEdit = !!editDevice

  const [step, setStep] = useState<1 | 2>(isEdit ? 2 : 1)
  const [selectedMacId, setSelectedMacId] = useState<string | null>(null)
  const [location, setLocation] = useState<number | ''>('')
  const [deviceModel, setDeviceModel] = useState<string>('')

  const [selectedModel, setSelectedModel] = useState<null | {
    label: string
    channels: { id: number; name?: string }[]
  }>(null)

  const [deviceNames, setDeviceNames] = useState<DeviceNameEntry[]>([])

  const { user } = useUser()
  const [createDevice] = useCreateDeviceMutation()
  const [updateDevice] = useUpdateDeviceMutation()
  const { data: locationsData, isLoading: locationsLoading } = useGetAllLocationsQuery(
    {},
    { skip: !user },
  )
  const { data: deviceModelsData } = useGetDeviceModelsQuery()

  const locationItems =
    locationsData?.results.map((l: any) => ({ id: l.id, name: l.title })) ?? []

  const deviceModelItems =
    deviceModelsData?.results.map((m: any) => ({
      id: String(m.id),
      name: m.title,
      deviceCount: m.channels.length,
    })) ?? []

  const buildStateFromDevice = (dev: any) => {
    const modelIdStr = dev?.model?.id ? String(dev.model.id) : ''

    setStep(2)
    setSelectedMacId(dev?.physical_id ?? null)
    setLocation(dev?.location?.id ?? '')
    setDeviceModel(modelIdStr)

    const modelFromApi =
      deviceModelsData?.results?.find((m: any) => String(m.id) === modelIdStr) ??
      dev?.model ??
      null

    if (!modelFromApi) {
      setSelectedModel(null)
      setDeviceNames([])
      return
    }

    setSelectedModel({ label: modelFromApi.title, channels: modelFromApi.channels })

    const existing =
      dev?.device_channels ??
      (dev?.model_channel
        ? [{ model_channel_id: dev.model_channel.id, title: dev.title }]
        : [])

    setDeviceNames(
      (modelFromApi.channels ?? []).map((ch: any) => {
        const match = existing.find((d: any) => d.model_channel_id === ch.id)
        return {
          name: match ? match.title : '',
          checked: !!match,
        }
      }),
    )
  }

  useEffect(() => {
    if (!isOpen) return

    if (isEdit && editDevice) {
      buildStateFromDevice(editDevice)
    } else {
      setStep(1)
      setSelectedMacId(null)
      setLocation('')
      setDeviceModel('')
      setSelectedModel(null)
      setDeviceNames([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEdit, editDevice?.id, deviceModelsData])

  const handleModelChange = (modelIdStr: string) => {
    setDeviceModel(modelIdStr)
    const model =
      deviceModelsData?.results?.find((m: any) => String(m.id) === modelIdStr) ?? null
    if (!model) {
      setSelectedModel(null)
      setDeviceNames([])
      return
    }
    setSelectedModel({ label: model.title, channels: model.channels })
    setDeviceNames(
      model.channels.map(() => ({
        name: '',
        checked: false,
      })),
    )
  }

  const atLeastOneCheckedWithName = deviceNames.some(
    (d) => d.checked && d.name.trim() !== '',
  )
  const canSave = !!location && !!selectedModel && atLeastOneCheckedWithName

  const handleRegister = async () => {
    const toastId = toast.info(
      isEdit ? 'Saving changes...' : 'Setting up your device...',
      {
        style: { padding: '10px 20px' },
        autoClose: false,
        isLoading: true,
      },
    )

    try {
      const checkedIdx = deviceNames
        .map((d, i) => (d.checked ? i : -1))
        .filter((i) => i !== -1)

      const device_channels = checkedIdx.map((idx) => ({
        model_channel_id: selectedModel!.channels[idx].id,
        title: deviceNames[idx].name || selectedModel!.channels[idx].name,
      }))

      if (isEdit) {
        await updateDevice({
          id: editDevice.id,
          physical_id: selectedMacId,
          location,
          model: Number(deviceModel),
          device_channels,
        }).unwrap()

        toast.update(toastId, {
          render: 'Changes saved',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        })
      } else {
        await createDevice({
          physical_id: selectedMacId,
          model: Number(deviceModel),
          location,
          is_virtual: false,
          device_channels,
        }).unwrap()

        toast.update(toastId, {
          render: 'Successfully registered',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        })
      }

      onClose()
    } catch (e: any) {
      toast.update(toastId, {
        render: e?.data?.detail || 'Something went wrong please try again!',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent borderRadius="3xl">
        <ModalHeader>
          {isEdit
            ? 'Edit Device'
            : step === 1
            ? 'Register physical device/s'
            : `Add ${selectedMacId}`}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody p={0} display="flex" flexDirection="column">
          {!isEdit && step === 1 && (
            <Box px={4} py={2}>
              {data?.count === 0 && (
                <Text color="gray.500" fontStyle="italic">
                  No devices found.
                </Text>
              )}
              {data?.results.map((d) => {
                return (
                  <HStack
                    key={d.mac_id}
                    justify="space-between"
                    py={3}
                    borderBottom="1px solid"
                    borderColor="gray.200"
                  >
                    <Box>
                      <Text fontWeight="medium" color="gray.600">
                        {d.mac_id}
                      </Text>

                      {d.suggested_model?.title && (
                        <Text fontSize="sm" color="gray.500" mt={1}>
                          Suggested model:{' '}
                          <Text as="span" fontWeight="semibold" color="gray.700">
                            {d.suggested_model.title}
                          </Text>
                        </Text>
                      )}
                    </Box>

                    <Button
                      size="sm"
                      onClick={() => {
                        setSelectedMacId(d.mac_id)
                        setStep(2)
                      }}
                    >
                      Register
                    </Button>
                  </HStack>
                )
              })}
            </Box>
          )}

          {(isEdit || step === 2) && (
            <VStack spacing={4} align="stretch" px={6} py={4}>
              {isEdit && (
                <>
                  <Text fontWeight="light">Physical ID</Text>
                  <Input
                    value={selectedMacId || ''}
                    placeholder="Enter MAC ID"
                    onChange={(e) => setSelectedMacId(e.target.value)}
                  />
                </>
              )}

              <Text fontWeight="light">Location</Text>
              {locationsLoading ? (
                <Spinner />
              ) : (
                <SingleSelect
                  items={locationItems}
                  selectedOption={location}
                  setSelectedOption={(val) => setLocation(val as any)}
                  header="Select a location"
                  required={!location}
                />
              )}

              <Text fontWeight="light">Device Model</Text>
              <SingleSelect
                items={deviceModelItems}
                selectedOption={deviceModel}
                setSelectedOption={(val) => handleModelChange(val as string)}
                header="Select a device model"
                required={!deviceModel}
              />

              {selectedModel && (
                <>
                  <Text fontWeight="light">Devices</Text>
                  {deviceNames.map((entry, index) => (
                    <HStack key={selectedModel.channels[index].id} spacing={4}>
                      <Text minW="120px">
                        {selectedModel.channels[index].name || `Channel #${index + 1}`}
                      </Text>
                      <Checkbox
                        isChecked={entry.checked}
                        onChange={(e) => {
                          const next = [...deviceNames]
                          next[index].checked = e.target.checked
                          if (!e.target.checked) next[index].name = ''
                          setDeviceNames(next)
                        }}
                      />
                      <Input
                        value={entry.name}
                        placeholder="Enter device name"
                        onChange={(e) => {
                          const next = [...deviceNames]
                          next[index].name = e.target.value
                          setDeviceNames(next)
                        }}
                        isDisabled={!entry.checked}
                      />
                    </HStack>
                  ))}
                </>
              )}
            </VStack>
          )}
        </ModalBody>

        {(isEdit || step === 2) && (
          <ModalFooter gap={3}>
            {!isEdit && (
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
            )}
            <Button
              colorScheme="button.primary"
              onClick={handleRegister}
              isDisabled={!canSave}
            >
              {isEdit ? 'Save changes' : 'Register'}
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  )
}
